import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { HC } from "./theme";
import { Card, SectionHead, Btn, Table, Empty, Pill, Select } from "./ui";
import { toast } from "sonner";
import { Search, KeyRound, Power, RefreshCw, Smartphone, Copy, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { fmtDateTime } from "../../lib/format";

function StatBox({ label, value, tint, testId }) {
  return (
    <div className="rounded-2xl border p-4 flex flex-col gap-1" style={{ borderColor: HC.border, background: HC.panel }} data-testid={testId}>
      <div className="text-[11px] font-bold tracking-[0.14em]" style={{ color: HC.soft }}>{label}</div>
      <div className="font-brand text-3xl font-bold" style={{ color: tint || HC.text }}>{value}</div>
    </div>
  );
}

export default function StaffPortalAccess() {
  const [data, setData] = useState({ rows: [], total: 0, provisioned: 0, pending: 0 });
  const [resets, setResets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState("");
  const [branch, setBranch] = useState("");
  const [state, setState] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const reload = async () => {
    try {
      const d = await api.get("/hr/iam/staff-portal", { params: {
        q: search || undefined,
        location_id: branch || undefined,
        provisioned: state === "" ? undefined : state === "yes",
      } });
      setData(d);
      setSelected((s) => s.filter((id) => d.rows.some((r) => r.employee_id === id)));
    } catch (e) { toast.error(formatApiError(e)); }
    api.get("/hr/iam/staff-portal/reset-requests", { params: { status: "pending" } })
      .then(setResets).catch(() => {});
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [search, branch, state]);
  useEffect(() => { api.get("/locations").then(setLocations).catch(() => {}); }, []);

  const pendingRows = useMemo(() => data.rows.filter((r) => !r.provisioned), [data.rows]);
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const provision = async (ids) => {
    if (!ids.length) { toast.error("Select at least one employee"); return; }
    setBusy(true);
    try {
      const r = await api.post("/hr/iam/staff-portal/provision", { employee_ids: ids });
      setResults(r.results || []);
      toast.success(`${r.provisioned} provisioned · ${r.reissued} reissued`);
      setSelected([]); reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const resetOne = async (row) => {
    try {
      const r = await api.post(`/hr/iam/staff-portal/${row.employee_id}/reset-password`);
      setResults([{ name: row.name, login_email: r.login_email, temp_password: r.temp_password, status: "reset" }]);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const revokeOne = async (row) => {
    try { await api.post(`/hr/iam/staff-portal/${row.employee_id}/revoke`); toast.success(`Portal access revoked for ${row.name}`); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const restoreOne = async (row) => {
    try { await api.post(`/hr/iam/staff-portal/${row.employee_id}/restore`); toast.success(`Portal access restored for ${row.name}`); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const cancelReset = async (r) => {
    try { await api.post(`/hr/iam/staff-portal/reset-requests/${r.id}/cancel`); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="iam-staff-portal">
      {resets.length > 0 && (
        <Card testid="sp-reset-requests">
          <SectionHead title={`Password reset requests (${resets.length})`}
            sub="Staff tapped 'Forgot password' in the portal — read the code out to them. It lasts 30 minutes and works once." />
          <div className="space-y-2">
            {resets.map((r) => (
              <div key={r.id} className="rounded-xl border p-3 flex flex-wrap items-center gap-3"
                style={{ borderColor: HC.border, background: r.expired ? "#F7E5E5" : HC.panelSoft }} data-testid={`sp-reset-req-${r.id}`}>
                <div className="min-w-0 mr-auto">
                  <div className="font-semibold text-sm" style={{ color: HC.text }}>{r.name}
                    <span className="ml-2 text-[11px] font-normal" style={{ color: HC.soft }}>{r.employee_no || "—"} · {r.branch}</span></div>
                  <div className="text-[11.5px]" style={{ color: HC.muted }}>{r.email} · requested {(r.created_at || "").slice(11, 16)} · expires {(r.expires_at || "").slice(11, 16)}
                    {r.attempts ? ` · ${r.attempts} wrong attempt(s)` : ""}</div>
                </div>
                {r.expired
                  ? <span className="text-xs font-bold" style={{ color: HC.red }}>EXPIRED</span>
                  : <span className="font-mono font-bold text-2xl tracking-[0.3em]" style={{ color: HC.burgundy }} data-testid={`sp-reset-code-${r.id}`}>{r.code}</span>}
                <Btn variant="outline" className="h-8" onClick={() => cancelReset(r)} data-testid={`sp-reset-cancel-${r.id}`}>Dismiss</Btn>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatBox label="EMPLOYEES" value={data.total} testId="sp-stat-total" />
        <StatBox label="PORTAL ACCESS PROVISIONED" value={data.provisioned} tint={HC.green || "#4F7A52"} testId="sp-stat-provisioned" />
        <StatBox label="AWAITING PROVISIONING" value={data.pending} tint={HC.gold} testId="sp-stat-pending" />
      </div>

      <Card>
        <SectionHead title="Staff Portal access"
          sub="Each employee gets their own portal login — separate from their attendance PIN and POS PIN. Temporary passwords are shown once; staff must change them on first sign-in."
          right={<div className="flex flex-wrap gap-2">
            <Btn variant="outline" className="h-9" onClick={() => provision(pendingRows.map((r) => r.employee_id))}
              disabled={busy || !pendingRows.length} data-testid="sp-provision-all">
              <Smartphone size={14} /> Provision all pending ({pendingRows.length})
            </Btn>
            <Btn className="h-9" onClick={() => provision(selected)} disabled={busy || !selected.length} data-testid="sp-provision-selected">
              <KeyRound size={14} /> Provision selected ({selected.length})
            </Btn>
          </div>} />

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="flex items-center gap-2 border rounded-lg h-9 px-3 bg-white min-w-[240px]" style={{ borderColor: HC.border }}>
            <Search size={14} style={{ color: HC.soft }} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, staff no or email…"
              className="bg-transparent flex-1 outline-none text-sm" data-testid="sp-search" />
          </div>
          <Select value={branch} onChange={(e) => setBranch(e.target.value)} data-testid="sp-branch-filter">
            <option value="">All branches</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
          <Select value={state} onChange={(e) => setState(e.target.value)} data-testid="sp-state-filter">
            <option value="">All employees</option>
            <option value="yes">Has portal access</option>
            <option value="no">No portal access</option>
          </Select>
          <button onClick={reload} className="ml-auto text-xs font-semibold flex items-center gap-1" style={{ color: HC.burgundy }} data-testid="sp-refresh">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {data.rows.length === 0 ? <Empty text="No employees match this filter." /> : (
          <Table
            testid="sp-table" rows={data.rows} rowKey={(r) => r.employee_id} empty="No employees"
            columns={[
              { key: "_sel", label: "", render: (r) => r.provisioned ? <span style={{ color: HC.soft }}>—</span> : (
                  <input type="checkbox" checked={selected.includes(r.employee_id)} onChange={() => toggle(r.employee_id)}
                    data-testid={`sp-select-${r.employee_id}`} />) },
              { key: "name", label: "EMPLOYEE", render: (r) => (
                  <span><span className="font-medium" style={{ color: HC.text }}>{r.name}</span>
                    <span className="block text-[11px]" style={{ color: HC.soft }}>{r.employee_no} · {r.position}</span></span>) },
              { key: "branch", label: "BRANCH" },
              { key: "login_email", label: "PORTAL LOGIN", render: (r) => r.login_email || <span style={{ color: HC.soft }}>Not provisioned</span> },
              { key: "provisioned", label: "ACCESS", render: (r) => (
                  <Pill status={!r.provisioned ? "absent" : r.login_status === "active" ? "active" : "absent"}
                    text={!r.provisioned ? "None" : r.login_status === "active" ? "Active" : "Revoked"} />) },
              { key: "must_reset_password", label: "PASSWORD", render: (r) => !r.provisioned ? "—" : (r.must_reset_password ? "Temporary" : "Set by staff") },
              { key: "last_login", label: "LAST SIGN-IN", render: (r) => r.last_login ? fmtDateTime(r.last_login) : "Never" },
              { key: "_a", label: "", render: (r) => (
                  <span className="flex items-center gap-3 text-[11.5px] font-semibold">
                    {!r.provisioned && <button onClick={() => provision([r.employee_id])} data-testid={`sp-provision-${r.employee_id}`} style={{ color: HC.burgundy }}>Provision</button>}
                    {r.provisioned && <button onClick={() => resetOne(r)} data-testid={`sp-reset-${r.employee_id}`} className="inline-flex items-center gap-1" style={{ color: HC.burgundy }}><KeyRound size={12} /> Reset</button>}
                    {r.provisioned && r.login_status === "active" && <button onClick={() => revokeOne(r)} data-testid={`sp-revoke-${r.employee_id}`} className="inline-flex items-center gap-1" style={{ color: HC.red }}><Power size={12} /> Revoke</button>}
                    {r.provisioned && r.login_status !== "active" && <button onClick={() => restoreOne(r)} data-testid={`sp-restore-${r.employee_id}`} className="inline-flex items-center gap-1" style={{ color: "#4F7A52" }}><RotateCcw size={12} /> Restore</button>}
                  </span>) },
            ]}
          />
        )}
      </Card>

      <Dialog open={!!results} onOpenChange={(o) => !o && setResults(null)}>
        <DialogContent className="max-w-lg" data-testid="sp-credentials-dialog">
          <DialogHeader>
            <DialogTitle>Temporary credentials</DialogTitle>
            <DialogDescription>Shown once only — hand these over now. Staff must change the password on first sign-in at /staff.</DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-2">
            {(results || []).map((r, i) => (
              <div key={i} className="rounded-lg border p-3" style={{ borderColor: HC.border, background: HC.panelSoft }} data-testid={`sp-credential-${i}`}>
                {r.status === "error" ? (
                  <div className="text-sm" style={{ color: HC.red }}>{r.employee_id}: {r.detail}</div>
                ) : (
                  <>
                    <div className="font-semibold text-sm" style={{ color: HC.text }}>{r.name}</div>
                    <div className="text-[12px] mt-1" style={{ color: HC.muted }}>Login: <span className="font-mono">{r.login_email}</span></div>
                    <div className="text-[12px] flex items-center gap-2" style={{ color: HC.muted }}>
                      Password: <span className="font-mono font-bold" style={{ color: HC.burgundy }}>{r.temp_password}</span>
                      <button onClick={() => { navigator.clipboard?.writeText(`${r.login_email} / ${r.temp_password}`); toast.success("Copied"); }}
                        data-testid={`sp-copy-${i}`} style={{ color: HC.burgundy }}><Copy size={12} /></button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          <DialogFooter><Btn variant="outline" onClick={() => setResults(null)} data-testid="sp-credentials-close">Done</Btn></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
